import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    TextField,
    Button,
    Chip,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Paper,
    Tab,
    Tabs,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    AttachFile as AttachFileIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Flag as PriorityIcon,
    Label as LabelIcon,
    CheckBox as ChecklistIcon,
    Comment as CommentIcon,
    Link as LinkIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCatAssistant } from '../contexts/CatAssistantContext';

interface Task {
    id: number;
    title: string;
    description: string;
    status: 'todo' | 'in_progress' | 'blocked' | 'done';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    position: number;
    projectId: number;
    assignedTo?: {
        id: number;
        name: string;
        email: string;
    };
    createdBy: {
        id: number;
        name: string;
        email: string;
    };
    startDate?: string;
    dueDate?: string;
    labels: string[];
    subtasks: Subtask[];
    comments: Comment[];
    reservation?: {
        id: number;
        customerName: string;
        startDate: string;
        endDate: string;
    };
    client?: {
        id: number;
        name: string;
        email: string;
    };
    estimatedHours?: number;
    actualHours?: number;
    attachments?: {
        id: number;
        filename: string;
        originalName: string;
        url: string;
        fileSize: number;
        uploader: {
            id: number;
            name: string;
        };
        createdAt: string;
    }[];
}

interface Subtask {
    id: number;
    title: string;
    isCompleted: boolean;
    completedBy?: {
        id: number;
        name: string;
    };
    completedAt?: string;
}

interface Comment {
    id: number;
    content: string;
    author: {
        id: number;
        name: string;
        email: string;
    };
    createdAt: string;
    mentions: number[];
}

interface ProjectMember {
    id: number;
    user: {
        id: number;
        name: string;
        email: string;
    };
}

interface TaskDetailPanelProps {
    taskId: number | null;
    open: boolean;
    onClose: () => void;
    onUpdate: (updatedTask: Task) => void;
}

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({ taskId, open, onClose, onUpdate }) => {
    const { user } = useAuth();
    const { commentAdded, subtaskCompleted } = useCatAssistant();
    const [task, setTask] = useState<Task | null>(null);
    const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [editMode, setEditMode] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
    const [assignedTo, setAssignedTo] = useState<number | undefined>();
    const [dueDate, setDueDate] = useState('');
    const [labels, setLabels] = useState<string[]>([]);
    const [newLabel, setNewLabel] = useState('');
    const [newComment, setNewComment] = useState('');
    const [newSubtask, setNewSubtask] = useState('');

    const [estimatedHours, setEstimatedHours] = useState('');
    const [actualHours, setActualHours] = useState('');
    const [attachments, setAttachments] = useState<NonNullable<Task['attachments']>>([]);
    const [isAutoGeneratingSubtasks, setIsAutoGeneratingSubtasks] = useState(false);

    const handleAutoGenerateSubtasks = async () => {
        if (!taskId) return;
        try {
            setIsAutoGeneratingSubtasks(true);
            const res = await axios.post(`/api/ai/tasks/${taskId}/auto-subtasks`);
            if (res.data?.subtasks) {
                setTask(prev => prev ? {
                    ...prev,
                    subtasks: [...(prev.subtasks || []), ...res.data.subtasks]
                } : null);
                window.dispatchEvent(new CustomEvent('taskDataChanged'));
            }
        } catch (err) {
            console.error('Error auto-generating subtasks:', err);
        } finally {
            setIsAutoGeneratingSubtasks(false);
        }
    };

    const priorityColors = {
        low: '#10B981',
        medium: '#3B82F6',
        high: '#F59E0B',
        urgent: '#EF4444'
    };

    useEffect(() => {
        if (taskId && open) {
            fetchTaskDetails();
        }
    }, [taskId, open]);

    const fetchTaskDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            // First fetch task details to get the projectId
            const taskResponse = await axios.get(`/api/tasks/${taskId}`);
            const taskData = taskResponse.data;

            // Now fetch comments, subtasks, and members using the real projectId
            const [commentsResponse, subtasksResponse, membersResponse] = await Promise.all([
                axios.get(`/api/tasks/${taskId}/comments`),
                axios.get(`/api/tasks/${taskId}/subtasks`),
                axios.get(`/api/tasks/projects/${taskData.projectId}/members`)
            ]);

            setTask({ ...taskData, comments: commentsResponse.data, subtasks: subtasksResponse.data });
            setProjectMembers(membersResponse.data);
            setAttachments(taskData.attachments || []);

            // Set form state
            setTitle(taskData.title);
            setDescription(taskData.description);
            setPriority(taskData.priority);
            setAssignedTo(taskData.assignedTo?.id);
            setDueDate(taskData.dueDate ? taskData.dueDate.split('T')[0] : '');
            setLabels(taskData.labels || []);
            setEstimatedHours(taskData.estimatedHours || '');
            setActualHours(taskData.actualHours || '');
        } catch (err: any) {
            console.error('Error fetching task details:', err);
            setError(err.response?.data?.error || 'Failed to load task details');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            const updatedData = {
                title,
                description,
                priority,
                assignedTo,
                dueDate: dueDate || null,
                labels,
                estimatedHours: estimatedHours ? parseFloat(estimatedHours) : null,
                actualHours: actualHours ? parseFloat(actualHours) : null
            };

            const response = await axios.put(`/api/tasks/${taskId}`, updatedData);
            setTask(response.data);
            onUpdate(response.data);
            setEditMode(false);
        } catch (err: any) {
            console.error('Error updating task:', err);
            setError(err.response?.data?.error || 'Failed to update task');
        } finally {
            setSaving(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        try {
            const response = await axios.post(`/api/tasks/${taskId}/comments`, {
                content: newComment
            });

            setTask(prev => prev ? {
                ...prev,
                comments: [...(prev.comments || []), response.data]
            } : null);

            setNewComment('');
            // 🐱 Cat reaction!
            commentAdded();
        } catch (err: any) {
            console.error('Error adding comment:', err);
            setError(err.response?.data?.error || 'Failed to add comment');
        }
    };

    const handleAddSubtask = async () => {
        if (!newSubtask.trim()) return;

        try {
            const response = await axios.post(`/api/tasks/${taskId}/subtasks`, {
                title: newSubtask
            });

            setTask(prev => prev ? {
                ...prev,
                subtasks: [...(prev.subtasks || []), response.data]
            } : null);

            setNewSubtask('');
        } catch (err: any) {
            console.error('Error adding subtask:', err);
            setError(err.response?.data?.error || 'Failed to add subtask');
        }
    };

    const handleToggleSubtask = async (subtaskId: number, isCompleted: boolean) => {
        try {
            await axios.put(`/api/tasks/subtasks/${subtaskId}`, {
                isCompleted
            });

            setTask(prev => prev ? {
                ...prev,
                subtasks: (prev.subtasks || []).map(st =>
                    st.id === subtaskId
                        ? {
                            ...st,
                            isCompleted,
                            completedBy: isCompleted && user ? { id: parseInt(user.id), name: user.name } : undefined,
                            completedAt: isCompleted ? new Date().toISOString() : undefined
                        }
                        : st
                )
            } : null);

            // 🐱 Cat reaction - count remaining uncompleted subtasks
            const remaining = task ? (task.subtasks || []).filter(st => st.id !== subtaskId && !st.isCompleted).length + (isCompleted ? 0 : 1) : 0;
            subtaskCompleted(remaining);
        } catch (err: any) {
            console.error('Error toggling subtask:', err);
            setError(err.response?.data?.error || 'Failed to update subtask');
        }
    };

    const handleAddLabel = () => {
        if (newLabel.trim() && !labels.includes(newLabel.trim())) {
            setLabels([...labels, newLabel.trim()]);
            setNewLabel('');
        }
    };

    const handleRemoveLabel = (labelToRemove: string) => {
        setLabels(labels.filter(label => label !== labelToRemove));
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const file = event.target.files[0];
        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const response = await axios.post(`/api/tasks/${taskId}/attachments`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setAttachments([...attachments, response.data]);
        } catch (err: any) {
            console.error('Error uploading file:', err);
            setError(err.response?.data?.error || 'Failed to upload file');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAttachment = async (attachmentId: number) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este archivo?')) return;

        try {
            await axios.delete(`/api/tasks/attachments/${attachmentId}`);
            setAttachments(attachments.filter(a => a.id !== attachmentId));
        } catch (err: any) {
            console.error('Error deleting attachment:', err);
            setError(err.response?.data?.error || 'Failed to delete attachment');
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: es });
        } catch {
            return 'Fecha inválida';
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!open) return null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100vw', sm: 600 }, maxWidth: '100vw', zIndex: 9999, top: { xs: 0, sm: '80px' }, height: { xs: '100vh', sm: 'calc(100vh - 80px)' } }
            }}
        >
            <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {loading && !task ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : task ? (
                    <>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Box sx={{ flex: 1 }}>
                                {editMode ? (
                                    <TextField
                                        fullWidth
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mb: 2 }}
                                    />
                                ) : (
                                    <Typography variant="h5" component="h2" gutterBottom>
                                        {task.title}
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Chip
                                        label={task.priority}
                                        size="small"
                                        sx={{
                                            bgcolor: priorityColors[task.priority],
                                            color: 'white',
                                            textTransform: 'capitalize'
                                        }}
                                    />
                                    <Chip
                                        label={task.status.replace('_', ' ')}
                                        size="small"
                                        variant="outlined"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <IconButton onClick={() => setEditMode(!editMode)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Tabs */}
                        <Paper sx={{ mb: 2 }}>
                            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="fullWidth">
                                <Tab label="Detalles" />
                                <Tab label={`Subtareas (${task.subtasks?.length || 0})`} />
                                <Tab label={`Adjuntos (${attachments.length})`} />
                                <Tab label={`Comentarios (${task.comments?.length || 0})`} />
                            </Tabs>
                        </Paper>

                        {/* Tab Content */}
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {activeTab === 0 && (
                                <Box>
                                    {/* Description */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Descripción
                                        </Typography>
                                        {editMode ? (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {task.description || 'Sin descripción'}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Assignment */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Asignado a
                                        </Typography>
                                        {editMode ? (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Asignar a</InputLabel>
                                                <Select
                                                    value={assignedTo || ''}
                                                    onChange={(e) => setAssignedTo(e.target.value || undefined)}
                                                    label="Asignar a"
                                                >
                                                    <MenuItem value="">Sin asignar</MenuItem>
                                                    {projectMembers.map((member) => (
                                                        <MenuItem key={member.user.id} value={member.user.id}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                                                                    {member.user.name.charAt(0)}
                                                                </Avatar>
                                                                {member.user.name}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : task.assignedTo ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Avatar sx={{ width: 32, height: 32, mr: 2, fontSize: '0.875rem' }}>
                                                    {task.assignedTo.name.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2">{task.assignedTo.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {task.assignedTo.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Sin asignar
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Time Tracking */}
                                    {(editMode || estimatedHours || actualHours) && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Seguimiento de Tiempo (Horas)
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                {editMode ? (
                                                    <>
                                                        <TextField
                                                            label="Estimado"
                                                            type="number"
                                                            value={estimatedHours}
                                                            onChange={(e) => setEstimatedHours(e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                        />
                                                        <TextField
                                                            label="Real"
                                                            type="number"
                                                            value={actualHours}
                                                            onChange={(e) => setActualHours(e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                        />
                                                    </>
                                                ) : (
                                                    <Box sx={{ display: 'flex', gap: 4 }}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Estimado</Typography>
                                                            <Typography variant="body1">{estimatedHours || '-'} h</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">Real</Typography>
                                                            <Typography variant="body1">{actualHours || '-'} h</Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Due Date */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Fecha de vencimiento
                                        </Typography>
                                        {editMode ? (
                                            <TextField
                                                fullWidth
                                                type="date"
                                                value={dueDate}
                                                onChange={(e) => setDueDate(e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        ) : task.dueDate ? (
                                            <Typography variant="body2">
                                                {format(new Date(task.dueDate), 'dd MMMM yyyy', { locale: es })}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Sin fecha de vencimiento
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Labels */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Etiquetas
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                            {labels.map((label) => (
                                                <Chip
                                                    key={label}
                                                    label={label}
                                                    size="small"
                                                    onDelete={editMode ? () => handleRemoveLabel(label) : undefined}
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                        {editMode && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField
                                                    size="small"
                                                    placeholder="Nueva etiqueta"
                                                    value={newLabel}
                                                    onChange={(e) => setNewLabel(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleAddLabel()}
                                                />
                                                <Button size="small" onClick={handleAddLabel}>
                                                    <AddIcon />
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Reservation Link */}
                                    {task.reservation && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Reserva asociada
                                            </Typography>
                                            <Chip
                                                label={`Reserva #${task.reservation.id} - ${task.reservation.customerName}`}
                                                color="primary"
                                                variant="outlined"
                                                onClick={() => {
                                                    // Navigate to reservation details
                                                    window.open(`/board/${task.reservation?.id}`, '_blank');
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {/* Save Button */}
                                    {editMode && (
                                        <Box sx={{ mt: 3 }}>
                                            <Button
                                                variant="contained"
                                                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                                onClick={handleSave}
                                                disabled={saving}
                                            >
                                                {saving ? 'Guardando...' : 'Guardar cambios'}
                                            </Button>
                                            <Button
                                                sx={{ ml: 1 }}
                                                onClick={() => {
                                                    setEditMode(false);
                                                    // Reset form state
                                                    setTitle(task.title);
                                                    setDescription(task.description);
                                                    setPriority(task.priority);
                                                    setAssignedTo(task.assignedTo?.id);
                                                    setDueDate(task.dueDate ? task.dueDate.split('T')[0] : '');
                                                    setLabels(task.labels || []);
                                                    setEstimatedHours(task.estimatedHours ? String(task.estimatedHours) : '');
                                                    setActualHours(task.actualHours ? String(task.actualHours) : '');
                                                }}
                                            >
                                                Cancelar
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={isAutoGeneratingSubtasks ? <CircularProgress size={16} color="inherit" /> : <span>🤖</span>}
                                        onClick={handleAutoGenerateSubtasks}
                                        disabled={isAutoGeneratingSubtasks}
                                        sx={{ mb: 2, borderRadius: 2.5, fontWeight: 700, textTransform: 'none', borderStyle: 'dashed' }}
                                    >
                                        {isAutoGeneratingSubtasks ? 'Desglosando tareas...' : '✨ Lyna, desglosar en subtareas automáticamente'}
                                    </Button>

                                    {/* Add Subtask */}
                                    <Box sx={{ mb: 3 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Nueva subtarea manual..."
                                            value={newSubtask}
                                            onChange={(e) => setNewSubtask(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask()}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton onClick={handleAddSubtask}>
                                                        <AddIcon />
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </Box>

                                    {/* Subtasks List */}
                                    <List>
                                        {(task.subtasks || []).map((subtask) => (
                                            <ListItem key={subtask.id} disablePadding>
                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleSubtask(subtask.id, !subtask.isCompleted)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <ChecklistIcon
                                                            color={subtask.isCompleted ? 'success' : 'disabled'}
                                                        />
                                                    </IconButton>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            textDecoration: subtask.isCompleted ? 'line-through' : 'none',
                                                            color: subtask.isCompleted ? 'text.secondary' : 'text.primary'
                                                        }}
                                                    >
                                                        {subtask.title}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {activeTab === 2 && (
                                <Box>
                                    {/* Upload Attachment */}
                                    <Box sx={{ mb: 3 }}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            startIcon={<AttachFileIcon />}
                                            fullWidth
                                        >
                                            Subir archivo
                                            <input
                                                type="file"
                                                hidden
                                                onChange={handleFileUpload}
                                            />
                                        </Button>
                                    </Box>

                                    {/* Attachments List */}
                                    <List>
                                        {attachments.map((attachment) => (
                                            <ListItem key={attachment.id}
                                                secondaryAction={
                                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAttachment(attachment.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <AttachFileIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <a href={`http://localhost:5000${attachment.url}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            {attachment.originalName}
                                                        </a>
                                                    }
                                                    secondary={`${formatFileSize(attachment.fileSize)} • Subido por ${attachment.uploader?.name || 'Desconocido'}`}
                                                />
                                            </ListItem>
                                        ))}
                                        {attachments.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                                No hay archivos adjuntos
                                            </Typography>
                                        )}
                                    </List>
                                </Box>
                            )}

                            {activeTab === 3 && (
                                <Box>
                                    {/* Add Comment */}
                                    <Box sx={{ mb: 3 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            placeholder="Agregar un comentario..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            InputProps={{
                                                endAdornment: (
                                                    <Button
                                                        size="small"
                                                        onClick={handleAddComment}
                                                        disabled={!newComment.trim()}
                                                    >
                                                        Comentar
                                                    </Button>
                                                )
                                            }}
                                        />
                                    </Box>

                                    {/* Comments List */}
                                    <List>
                                        {(task.comments || []).map((comment) => (
                                            <ListItem key={comment.id} alignItems="flex-start" sx={{ px: 0 }}>
                                                <ListItemAvatar>
                                                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                                                        {comment.author.name.charAt(0)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="subtitle2">
                                                                {comment.author.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatDate(comment.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                                            {comment.content}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    </>
                ) : null}
            </Box>
        </Drawer>
    );
};

export default TaskDetailPanel;